import './TrendingSunburst.scss';
import {
  ComposedChart,
  PendingContent,
  Spinner,
  SunburstChart,
} from 'plume-ui';
import { PendingContentStyles } from 'plume-ui/dist/components/PendingContent/PendingContent';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { useRecoilValue } from 'recoil';
import { locationCountAtom } from '../../../../state';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
import { refetchActiveQueries } from '../../../../utils/graphqlConfig';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import useCriteriaNames from '../../../Monitor/hooks/useCriteriaNames';
import { SBPieData } from '../../graphTypes';
import {
  HealthScorePieLayout,
  makeHealthBreakdown,
} from '../../services/healthBreakdownProc';
import {
  useGetPartnerHealthScoreInRange,
  useGetTypicalHealthScoreInRange,
} from '../../services/healthcheckServices';
import { isPieEmpty } from '../helpers';

type TrendingProps = {
  partner: string;
};
type TrendingStyles = {};

const pruneThreshold = 0.05;

const TrendingSunburst: AdjustableComponent<TrendingProps, TrendingStyles> = ({
  partner,
}) => {
  const locationCount = useRecoilValue(locationCountAtom);
  const [typicalPieChart, setTypicalPieChart] = useState<SBPieData[]>();
  const [partnerPieChart, setPartnerPieChart] = useState<SBPieData[]>();
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const { getCriteriaName } = useCriteriaNames();

  useLayoutEffect(() => {
    setWidth(ref.current?.offsetWidth || 0 / 2);
  }, []);

  const {
    baselineHealthStatsLoading,
    baselineHealthStats,
  } = useGetTypicalHealthScoreInRange();

  const {
    partnerHealthStatsLoading,
    partnerHealthStats,
  } = useGetPartnerHealthScoreInRange(partner);

  useEffect(() => {
    if (!partnerHealthStatsLoading) {
      if (partnerHealthStats)
        setPartnerPieChart(
          getPie(partnerHealthStats, locationCount || 1, 'environment'),
        );
      else setPartnerPieChart([]);
    }
  }, [partnerHealthStatsLoading, partnerHealthStats, locationCount]);

  useEffect(() => {
    if (!baselineHealthStatsLoading) {
      if (baselineHealthStats)
        setTypicalPieChart(
          getPie(baselineHealthStats, locationCount || 1, 'environment'),
        );
      else setTypicalPieChart([]);
    }
  }, [baselineHealthStatsLoading, baselineHealthStats, locationCount]);

  useEffect(() => {
    refetchActiveQueries();
  }, [partner]);

  const setSlice = (bd: any, id: string) => {
    if (id === '') {
      let biggest = 0;
      let bigId = '';

      bd.forEach((dat: any, id: number) => {
        if (dat.count > biggest) {
          biggest = dat.value;
          bigId = dat.id;
        }
      });
      bd.forEach((dat: any, id: number) => {
        if (dat.id === bigId) {
          if (dat?.children) {
            dat.children.forEach((child: any) => {
              if (child.value > pruneThreshold) {
                child.label = true;
              } else {
                child.label = false;
              }
            });
          }
        } else {
          delete dat.children;
        }
      });
      return bd;
    }
    const found = bd.find((bd: any) => bd.id === id);

    if (found?.children) {
      found.children.forEach((child: any) => {
        if (child.value > pruneThreshold) {
          child.label = true;
        } else {
          child.label = false;
        }
      });
    }

    bd.forEach((dat: any) => {
      if (dat.id !== id && dat?.children) {
        delete dat.children;
      }
    });
    return bd;
  };

  const getPie = (
    breakdown: any,
    locationCount: number,
    id: string,
  ): SBPieData[] => {
    const processedData = makeHealthBreakdown(
      breakdown,
      locationCount,
      getCriteriaName,
    );
    return setSlice(processedData, id);
  };

  const handleSliceClick = (slice: any) => {
    if (!slice.parent) {
      setTypicalPieChart(
        getPie(baselineHealthStats, locationCount || 1, slice.id),
      );
      setPartnerPieChart(
        getPie(partnerHealthStats, locationCount || 1, slice.id),
      );
    }
  };

  return (
    <div className="HealthSummary__sunburstContainer" ref={ref}>
      <div className="HealthSummary__Sunburst">
        <div className="HealthSummary__Sunburst__title">
          <FormattedMessage id="healthSummary.breakdown.myNetwork" />
        </div>

        <div className={isSafari === true ? 'HealthSummary__webkitFix' : ''}>
          <PendingContent
            loading={partnerHealthStatsLoading}
            loader={Spinner}
            classes={(current: PendingContentStyles) => ({
              ...current,
              root: `${current.root} HealthSummary__sunburstContainer__pendingContent`,
              loader: `${current.loader} HealthSummary__sunburstContainer__pendingContent__loader`,
            })}
            hideContent={false}
          >
            {!partnerHealthStatsLoading &&
              (isPieEmpty(partnerPieChart || []) ? (
                <div className="HealthSummary__sunburstContainer__noIssues-message">
                  <ComposedChart data={[]} groups={[]} />
                </div>
              ) : (
                <SunburstChart
                  data={partnerPieChart || []}
                  layout={HealthScorePieLayout}
                  width={width / 2}
                  height={250}
                  cx={width / 4}
                  cy={100}
                  perc={true}
                  selectedPie={handleSliceClick}
                  anim={true}
                />
              ))}
          </PendingContent>
        </div>
      </div>
      <div className="HealthSummary__Sunburst">
        <div className="HealthSummary__Sunburst__title">
          <FormattedMessage id="healthSummary.breakdown.typNetwork" />
        </div>
        <div className={isSafari === true ? 'HealthSummary__webkitFix' : ''}>
          <PendingContent
            loading={baselineHealthStatsLoading}
            loader={Spinner}
            classes={(current: PendingContentStyles) => ({
              ...current,
              root: `${current.root} HealthSummary__sunburstContainer__pendingContent`,
              loader: `${current.loader} HealthSummary__sunburstContainer__pendingContent__loader`,
            })}
          >
            {typicalPieChart !== undefined &&
              (isPieEmpty(typicalPieChart || []) ? (
                <div className="HealthSummary__sunburstContainer__noIssues-message">
                  <ComposedChart data={undefined} groups={[]} />
                </div>
              ) : (
                <SunburstChart
                  data={typicalPieChart || []}
                  layout={HealthScorePieLayout}
                  width={width / 2}
                  height={250}
                  cx={width / 4}
                  cy={100}
                  perc={true}
                  selectedPie={handleSliceClick}
                  anim={true}
                />
              ))}
          </PendingContent>
        </div>
      </div>
    </div>
  );
};

export default memo(TrendingSunburst);
